import { debounce } from '@cas/utils/debounce';
import { constrain } from '@cas/utils/numberHelpers';
import { Chart, ChartArea, Plugin } from 'chart.js';

type CustomRangePluginCallback = (minPercent: number, maxPercent: number) => void;

interface CustomRangePluginOptions {
  borderColor: string;
  backgroundColor: string;
  callback?: CustomRangePluginCallback;
}

// Store all positions as ratios for automatic resizing.
type RangeMarker = number | null; // 0 - 1 | null

export class CustomRange {
  private readonly _chart: Chart;
  private _options: CustomRangePluginOptions;
  private _isMarking = false;
  private _markers: [RangeMarker, RangeMarker] = [null, null];
  private _interimMarker: RangeMarker = null;

  static MIN_PERCENT = 0;
  static MAX_PERCENT = 1;

  constructor(chart: Chart, options: CustomRangePluginOptions) {
    this._chart = chart;
    this._options = options;

    this._bindListeners();
    this._addEventListeners();
  }

  private _getCanvas(): HTMLCanvasElement | null {
    return this._chart.canvas;
  }

  private _getContext(): CanvasRenderingContext2D | null {
    return this._chart.ctx;
  }

  private _getChartArea(): ChartArea {
    return this._chart.chartArea;
  }

  private _getIsInChartArea(x: number, y: number, leeway = 0): boolean {
    const { left, right, top, bottom } = this._getChartArea();
    return x >= left - leeway && x <= right + leeway && y >= top - leeway && y <= bottom + leeway;
  }

  // Return ratio of 0 - 1
  private _getPercentageX(x: number): number {
    const { left, width } = this._getChartArea();
    const percentageX = (x - left) / width;
    return constrain(percentageX, CustomRange.MIN_PERCENT, CustomRange.MAX_PERCENT);
  }

  private _onRangeSelected(): void {
    const [first, second] = this._markers;

    if (this._options.callback && first !== null && second !== null) {
      this._options.callback(Math.min(first, second), Math.max(first, second));
    }
  }

  private _clickHandler(event: MouseEvent): void {
    const { offsetX: x, offsetY: y } = event;

    if (this._getIsInChartArea(x, y, 8)) {
      const markerX = this._getPercentageX(x);

      if (!this._isMarking) {
        // start a new range selection
        this._isMarking = true;
        this._markers = [markerX, null];
        this._interimMarker = null;
      } else {
        // complete a range selection
        this._isMarking = false;
        this._markers[1] = markerX;
        this._onRangeSelected();
      }
    }
  }

  // Keep track of the possible time range area if the first marker has been placed.
  private _mouseMoveHandler(event: MouseEvent): void {
    const { offsetX: x, offsetY: y } = event;

    if (this._isMarking && this._getIsInChartArea(x, y)) {
      this._interimMarker = this._getPercentageX(x);
    }
  }

  // Cancel any active marking interactions if the mouse leaves the canvas.
  private _mouseLeaveHandler(): void {
    if (this._isMarking) {
      this.reset();
    }
  }

  private _bindListeners(): void {
    this._clickHandler = debounce(this._clickHandler.bind(this), 20);
    this._mouseMoveHandler = this._mouseMoveHandler.bind(this);
    this._mouseLeaveHandler = this._mouseLeaveHandler.bind(this);
  }

  private _addEventListeners(): void {
    const canvas = this._getCanvas();

    if (canvas) {
      canvas.addEventListener('click', this._clickHandler);
      canvas.addEventListener('mousemove', this._mouseMoveHandler);
      canvas.addEventListener('mouseleave', this._mouseLeaveHandler);
    }
  }

  private _removeEventListeners(): void {
    const canvas = this._getCanvas();

    if (canvas) {
      canvas.removeEventListener('click', this._clickHandler);
      canvas.removeEventListener('mousemove', this._mouseMoveHandler);
      canvas.removeEventListener('mouseleave', this._mouseMoveHandler);
    }
  }

  private _percentToX(percent: number): number {
    const { left, width } = this._getChartArea();
    return left + percent * width;
  }

  private _drawMarker(ctx: CanvasRenderingContext2D, xPercent: number): void {
    const { top, bottom } = this._getChartArea();
    const markerX = this._percentToX(xPercent);

    ctx.save();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = this._options.borderColor;
    ctx.lineWidth = 2;
    ctx.setLineDash([10, 5]);
    ctx.beginPath();
    ctx.moveTo(markerX, top);
    ctx.lineTo(markerX, bottom);
    ctx.stroke();
    ctx.restore();
  }

  private _drawAreaBox(
    ctx: CanvasRenderingContext2D,
    startPercent: number,
    endPercent: number,
  ): void {
    const { top, height } = this._getChartArea();
    const startX = this._percentToX(startPercent);
    const endX = this._percentToX(endPercent);
    const rectWidth = endX - startX;

    // draw rect background
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.fillStyle = this._options.backgroundColor;
    ctx.fillRect(startX, top, rectWidth, height);
    ctx.restore();
  }

  private _drawRange(ctx: CanvasRenderingContext2D) {
    if (this._markers[0] !== null) {
      const endPercent = this._markers[1] ?? this._interimMarker;

      if (endPercent !== null) {
        // draw background area box
        this._drawAreaBox(ctx, this._markers[0], endPercent);
      }

      // draw first marker line
      this._drawMarker(ctx, this._markers[0]);
    }

    if (this._markers[1] !== null) {
      // draw last marker line
      this._drawMarker(ctx, this._markers[1]);
    }
  }

  public reset(): void {
    this._isMarking = false;
    this._markers = [null, null];
    this._interimMarker = null;
  }

  public setOptions(options: CustomRangePluginOptions): void {
    this._options = options;
  }

  public draw(): void {
    const ctx = this._getContext();

    if (ctx) {
      this._drawRange(ctx);
    }
  }

  public stop(): void {
    this._removeEventListeners();
  }
}

export const CUSTOM_RANGE_PLUGIN_ID = 'customRange';

// Note: These settings appear to work well for both light and dark themes.
export const customRangeOptionsLight: CustomRangePluginOptions = {
  borderColor: '#007DBA',
  backgroundColor: 'rgba(128, 128, 128, 0.2)',
};

// Note: resize updates are not needed because marker positions are percentage based.
export const customRangePlugin: Plugin = {
  id: CUSTOM_RANGE_PLUGIN_ID,
  defaults: { ...customRangeOptionsLight },

  start(chart: Chart, args: object, options: CustomRangePluginOptions): void {
    // Create the custom range instance and add event listeners to the chart.
    chart.CustomRangeInstance = new CustomRange(chart, options);
  },

  afterEvent: (chart: Chart): void => {
    // Trigger the chart redraw.
    chart.draw();
  },

  beforeDraw: (chart: Chart): void => {
    // Draw the custom range markers behind the chart data and tooltips.
    chart.CustomRangeInstance?.draw();
  },

  beforeDatasetUpdate: (chart: Chart, args: object, options: CustomRangePluginOptions): void => {
    // Clear the custom range markers whenever the chart data is updated.
    chart.CustomRangeInstance?.reset();
    // Provide the updated callback and options to the plugin instance.
    chart.CustomRangeInstance?.setOptions(options);
  },

  stop(chart: Chart): void {
    // Remove event listeners and remove the custom range instance.
    chart.CustomRangeInstance?.stop();
    chart.CustomRangeInstance = undefined;
  },
};
